import { businessQuotesService } from '@/services/quotes/business';
import type { BusinessRisk } from '@/services/quotes/types';
import { RequiredTooltipProvider } from '@/shared/components/required-field-tooltip';
import { toaster } from '@/shared/ui/toaster.component';
import { Page, PageHeader } from '@/shared/ui/page';
import { downloadFromSignedUrl } from '@/shared/utils/signed-url.util';
import { Spinner, TabsComponent } from '@noorden-insurtech/alba-design-system';
import { useNavigate } from '@tanstack/react-router';
import { Home } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  StepCommercialization,
  StepInsuredData,
  StepRiskBusinessData,
} from '../../quotes/components/quote-steps';
import {
  QuoteParamsProvider,
  useQuoteParams,
} from '../../quotes/context/quote-params.context';
import {
  QuoteSummaryProvider,
  useQuoteSummary,
} from '../../quotes/context/quote-summary.context';
import { useCalculateAnalysis } from '../../quotes/hooks/business/use-calculate-analysis.hook';
import { useRejectAnalysis } from '../../quotes/hooks/business/use-reject-analysis.hook';
import { useSubmitAnalysisCalculation } from '../../quotes/hooks/business/use-submit-analysis-calculation.hook';
import { useUnderwritingMessages } from '../../quotes/hooks/business/use-underwriting-messages.hook';
import { useQuoteForm } from '../../quotes/hooks/common/use-quote-form.hook';
import { useProposalDetails } from '../../quotes/hooks/homeowner/use-proposal-details.hook';
import type { BusinessLocation } from '../../quotes/components/quote-steps/business-location';
import type { QuoteFormValues } from '../../quotes/types/quote-form.types';
import { mapBusinessRisksToLocations } from '../../quotes/utils/map-business-risks-to-locations.util';
import type {
  AnalysisCalculation,
  AnalysisLocation,
  AnalysisQuote,
} from '../types';
import { buildAnalysisPayload } from '../utils/build-analysis-payload';
import { AnalysisCriteriaAlert } from './analysis-criteria-alert.component';
import {
  Step4AnalysisCalculation,
  type DiscountLoading,
} from './step-4-analysis-calculation.component';

export type AnalysisTab =
  | 'insurance'
  | 'risk'
  | 'commercialization'
  | 'calculation';

const TAB_ORDER: AnalysisTab[] = [
  'insurance',
  'risk',
  'commercialization',
  'calculation',
];

interface AnalysisDetailsProps {
  policyId: string;
  initialTab?: AnalysisTab;
}

type ProposalDetailsData = NonNullable<
  ReturnType<typeof useProposalDetails>['data']
>;

type ProposalAgent = NonNullable<ProposalDetailsData['agents']>[number];

function buildLocationsFromProposal(
  proposal: ProposalDetailsData
): AnalysisLocation[] {
  const risks = proposal.lobs?.[0]?.risks ?? [];
  return risks.map((risk, index) => ({
    id: String(risk.pk ?? index),
    name: `Local ${String(index + 1).padStart(2, '0')}.`,
    street: risk.address ?? '',
    number: risk.addressNumber ?? '',
    complement: '',
    neighborhood: risk.neighborhood ?? '',
    city: risk.city ?? '',
    state: risk.state ?? '',
  }));
}

function buildCalculationFromProposal(
  proposal: ProposalDetailsData
): AnalysisCalculation {
  const total =
    proposal.grossPremium ?? proposal.GrossPremium ?? proposal.DuePremium ?? 0;
  const net = proposal.rawPremium ?? 0;
  const iof = proposal.iof ?? proposal.IOF ?? 0;
  // A corretagem total soma o corretor principal e os co-corretores (4º agent
  // em diante). O 2º e o 3º agents são entradas técnicas do InsureMO, não
  // corretores — mesma convenção de deriveCoBrokers.
  const commissionRate =
    proposal.agents?.[0]?.commissionRate !== undefined
      ? (proposal.agents ?? [])
          .filter((_, index) => index === 0 || index >= 3)
          .reduce(
            (total, agent) => total + (Number(agent.commissionRate) || 0),
            0
          ) * 100
      : typeof proposal.commissionRate === 'number'
        ? proposal.commissionRate * 100
        : 0;
  const shareRate = proposal.agents?.[0]?.shareRate ?? 0;

  return {
    totalPremium: Number(total) || 0,
    netPremium: Number(net) || 0,
    iof: Number(iof) || 0,
    commissionPercent: Number(commissionRate) || 0,
    participationPercent: Number(shareRate) * 100 || 0,
  };
}

function buildAnalysisQuote(
  policyId: string,
  proposal: ProposalDetailsData
): AnalysisQuote {
  const customer = proposal.customer;
  const agent = proposal.agents?.[0];

  return {
    id: policyId,
    proposerName: customer?.name ?? '',
    proposerDocument: customer?.idNo ?? customer?.cpf ?? '',
    brokerName: agent?.name ?? '',
    brokerDocument: '',
    product: 'Empresarial',
    productCode: proposal.productCode ?? '',
    productId:
      proposal.productId != null
        ? String(proposal.productId)
        : (proposal.productCode ?? ''),
    quoteNumber:
      proposal.proposalNo || proposal.quotationNo || proposal.policyId || '',
    transmissionDate: proposal.effectiveDate ?? '',
    quoteDate: proposal.quotationDate ?? proposal.proposalDate ?? '',
    status: 'pending',
    analysisStatus: 'under_review',
    responsibleId: undefined,
    responsibleName: undefined,
    criteria: [],
    locations: buildLocationsFromProposal(proposal),
    calculation: buildCalculationFromProposal(proposal),
  };
}

// Quando o step 3 nunca foi preenchido, o backend ainda devolve o corretor
// principal com commissionRate/shareRate = 0. Tratamos esse caso como
// "sem prefill" para que o StepCommercialization aplique o default
// (participação 100% e corretagem vazia), igual ao fluxo de nova cotação.
function deriveCommissionPercent(agent?: ProposalAgent): string {
  if (!agent || agent.commissionRate === undefined) return '';
  const shareRate = agent.shareRate || 0;
  const computed =
    shareRate > 0
      ? ((agent.commissionRate || 0) / shareRate) * 100
      : (agent.commissionRate || 0) * 100;
  return computed > 0 ? String(computed) : '';
}

function deriveParticipationPercent(agent?: ProposalAgent): string {
  if (!agent || agent.shareRate === undefined) return '';
  const computed = (agent.shareRate || 0) * 100;
  return computed > 0 ? String(computed) : '';
}

function deriveCoBrokers(proposal: ProposalDetailsData) {
  const agents = proposal.agents ?? [];
  if (agents.length <= 3) return [];
  return agents
    .slice(3)
    .filter(
      agent =>
        Boolean((agent.name ?? '').trim()) &&
        agent.shareRate !== undefined &&
        (agent.shareRate ?? 0) > 0
    )
    .map(agent => ({
      id: agent.agentCode ?? '',
      name: agent.name ?? '',
      shareRate: agent.shareRate,
    }));
}

export function AnalysisDetails({
  policyId,
  initialTab = 'insurance',
}: AnalysisDetailsProps) {
  const { data: proposal, isLoading, error } = useProposalDetails(policyId);

  const quote = useMemo(
    () => (proposal ? buildAnalysisQuote(policyId, proposal) : null),
    [policyId, proposal]
  );

  const breadcrumb = (
    <Page.Breadcrumb
      items={[
        {
          label: 'Pagina Inicial',
          href: '/dashboard',
          iconLeft: <Home className='h-4 w-4' />,
        },
        { label: 'Análise de Cotações', href: '/analyses' },
        { label: 'Análise', active: true },
      ]}
    />
  );

  if (isLoading) {
    return (
      <Page.Root>
        {breadcrumb}
        <Page.Content>
          <div className='flex w-full items-center justify-center p-8'>
            <Spinner size='lg' colorScheme='blue' />
          </div>
        </Page.Content>
      </Page.Root>
    );
  }

  if (error || !quote || !proposal) {
    return (
      <Page.Root>
        {breadcrumb}
        <Page.Content>
          <div className='flex flex-col gap-2 rounded-2xl bg-alba-white-500 p-8 shadow-[0px_4px_24px_0px_rgba(15,22,30,0.08)]'>
            <p className='font-montserrat text-base text-alba-black-500'>
              Cotação não encontrada.
            </p>
          </div>
        </Page.Content>
      </Page.Root>
    );
  }

  return (
    <Page.Root>
      {breadcrumb}

      <PageHeader.Root>
        <PageHeader.Main>
          <PageHeader.TitleGroup>
            <PageHeader.Title>
              Análise Cotação Empresarial - Nº {quote.quoteNumber}
            </PageHeader.Title>
            <PageHeader.Subtitle>
              Analise os dados e registros da inspeção para validação da cotação
            </PageHeader.Subtitle>
          </PageHeader.TitleGroup>
        </PageHeader.Main>
      </PageHeader.Root>

      <Page.Content className='py-4'>
        <QuoteParamsProvider>
          <QuoteSummaryProvider>
            <AnalysisStepsContent
              quote={quote}
              proposal={proposal}
              initialTab={initialTab}
              policyId={policyId}
            />
          </QuoteSummaryProvider>
        </QuoteParamsProvider>
      </Page.Content>
    </Page.Root>
  );
}

interface AnalysisStepsContentProps {
  quote: AnalysisQuote;
  proposal: ProposalDetailsData;
  initialTab: AnalysisTab;
  policyId: string;
}

function AnalysisStepsContent({
  quote,
  proposal,
  initialTab,
  policyId,
}: AnalysisStepsContentProps) {
  const navigate = useNavigate();
  const { setParams } = useQuoteParams();
  const {
    summary,
    updateProponent,
    updateInsuranceData,
    updateProperty,
    updateCommercialization,
  } = useQuoteSummary();
  const [activeTab, setActiveTab] = useState<AnalysisTab>(initialTab);
  const [isPrefilled, setIsPrefilled] = useState(false);
  const [analysisLocations, setAnalysisLocations] = useState<
    BusinessLocation[]
  >([]);
  const calculateAnalysis = useCalculateAnalysis();
  const rejectAnalysis = useRejectAnalysis();
  const submitAnalysisCalculation = useSubmitAnalysisCalculation();

  const { data: underwritingData } = useUnderwritingMessages(policyId);

  const underwritingMessages = useMemo(() => {
    return (underwritingData?.messages ?? [])
      .map(message => {
        const detail =
          message.messageDetail?.trim() || message.ruleName?.trim() || '';
        return detail;
      })
      .filter(Boolean);
  }, [underwritingData?.messages]);

  useEffect(() => {
    setParams({
      id: quote.id,
      policyId: quote.id,
      proposalNumber: quote.quoteNumber,
      productId: quote.productId,
      productCode: quote.productCode,
      category: 'BUSINESS_INSURANCE',
    });
  }, [quote, setParams]);

  const mainForm = useQuoteForm({
    defaultValues: {
      issuanceType: '1',
      startDate: '',
      endDate: '',
      isLegalPerson: true,
      name: '',
      hasSocialName: false,
      socialName: '',
      registerNumber: '',
      phone: '',
      email: '',
      policyNumber: '',
      cep: '',
      street: '',
      number: '',
      complement: '',
      neighborhood: '',
      city: '',
      state: '',
      propertyType: '',
      constructionType: '',
      propertyClassification: '',
      insuredObjectType: '',
      activityType: '',
      riskValue: '',
      isLandmarked: false,
      congener: '',
      bonus: '-1',
    } as Partial<QuoteFormValues>,
    onSubmit: async () => {
      // Submissão é tratada pelas steps de cotação reutilizadas.
    },
  });

  useEffect(() => {
    if (isPrefilled) return;
    const customer = proposal.customer;
    const risk = proposal.lobs?.[0]?.risks?.[0];
    const mainAgent = proposal.agents?.[0];

    const set = (field: string, value: unknown) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (mainForm as any).setFieldValue(field, value);
    };

    // O BFF/InsureMO devolve `idNo` como o TipoDocumento ("2" pra CNPJ),
    // não como o número. O documento real fica em `cpf`/`documentNumber`/`NoDocumento`.
    // Mantemos `idNo` como último fallback só pra não regredir cenários antigos.
    const resolvedDocument =
      customer?.cpf ??
      customer?.documentNumber ??
      customer?.idNo ??
      '';

    set('name', customer?.name ?? '');
    set('registerNumber', resolvedDocument);
    set('email', customer?.email ?? '');
    set('phone', customer?.cellphone ?? customer?.phone ?? '');
    set('cep', risk?.zipCode ?? customer?.zipCode ?? '');
    set('street', risk?.address ?? customer?.address ?? '');
    set('number', String(risk?.addressNumber ?? customer?.addressNumber ?? ''));
    set('complement', customer?.addressComplement ?? '');
    set('neighborhood', risk?.neighborhood ?? customer?.neighborhood ?? '');
    set('city', risk?.city ?? customer?.city ?? '');
    set('state', risk?.state ?? customer?.state ?? '');
    set('startDate', proposal.effectiveDate ?? '');
    set('endDate', proposal.expiryDate ?? proposal.terminateDate ?? '');
    set('propertyType', risk?.propertyType ?? '');
    set('constructionType', risk?.constructionType ?? '');
    set('propertyClassification', risk?.propertyClassification ?? '');
    set(
      'activityType',
      risk?.activityTypePerformed ?? risk?.tipoDeAtividadeRealizada ?? ''
    );
    set('insuredObjectType', risk?.scheduleExt?.[0]?.insuredObject ?? '');

    updateProponent({
      name: customer?.name ?? '',
      document: resolvedDocument,
      personType: 'PJ',
    });

    updateProperty({
      city: risk?.city ?? customer?.city ?? undefined,
      state: risk?.state ?? customer?.state ?? undefined,
      cep: risk?.zipCode ?? customer?.zipCode ?? undefined,
      address: risk?.address ?? customer?.address ?? undefined,
      number:
        String(risk?.addressNumber ?? customer?.addressNumber ?? '') ||
        undefined,
      neighborhood: risk?.neighborhood ?? customer?.neighborhood ?? undefined,
    });

    updateInsuranceData({
      validityStart: proposal.effectiveDate,
      validityEnd: proposal.expiryDate ?? proposal.terminateDate,
    });

    if (mainAgent) {
      updateCommercialization({
        brokerName: mainAgent.name ?? '',
        commissionPercent: deriveCommissionPercent(mainAgent),
        participationPercent: deriveParticipationPercent(mainAgent),
      });
    }

    setIsPrefilled(true);
  }, [
    proposal,
    mainForm,
    isPrefilled,
    updateProponent,
    updateProperty,
    updateInsuranceData,
    updateCommercialization,
  ]);

  const initialRisks = useMemo<BusinessRisk[]>(
    () => (proposal.lobs?.[0]?.risks ?? []) as BusinessRisk[],
    [proposal]
  );

  // Locais completos (coberturas, assistências, questionário) para o card
  // exibido no step 4. Quando o usuário ainda não editou nada no step de
  // risco, derivamos direto dos risks da proposta; caso já tenha editado,
  // priorizamos o estado local mantido pelo StepRiskBusinessData.
  const calculationLocations = useMemo<BusinessLocation[]>(() => {
    if (analysisLocations.length > 0) return analysisLocations;
    return mapBusinessRisksToLocations(initialRisks, { stableIds: true });
  }, [analysisLocations, initialRisks]);

  // Quando o calculate-analysis retorna, usamos os valores da própria resposta
  // (calcResult do Insuremo) para alimentar o Step4. O GET /proposal-details
  // muitas vezes devolve a versão original do quote, não a VersionInAnalysis
  // recalculada — então não dá pra depender do refetch.
  const calculatedQuote = calculateAnalysis.data;
  const effectiveCalculation = useMemo(() => {
    if (!calculatedQuote) return quote.calculation;
    // O CommissionRate devolvido pelo calculate é o consolidado da policy no
    // InsureMO e inclui os agents técnicos (2º e 3º, que não são corretores),
    // inflando a corretagem. Usa a corretagem do step de Comercialização
    // quando preenchida; senão, a derivada dos agents corretores da proposta.
    const formCommissionPercent = parseFloat(
      String(summary.commercialization.commissionPercent ?? '').replace(
        ',',
        '.'
      )
    );
    return {
      totalPremium: Number(calculatedQuote.GrossPremium),
      // Prêmio Líquido exibido é o RawPremium (não o BeforeVatPremium/netPremium);
      // quando o calculate não retorna RawPremium, usamos o PremioTarifario.
      netPremium: Number(
        calculatedQuote.RawPremium ?? calculatedQuote.PremioTarifario
      ),
      iof: Number(calculatedQuote.IOF),
      commissionPercent:
        Number.isFinite(formCommissionPercent) && formCommissionPercent > 0
          ? formCommissionPercent
          : quote.calculation.commissionPercent,
      participationPercent: quote.calculation.participationPercent,
    };
  }, [
    calculatedQuote,
    quote.calculation,
    summary.commercialization.commissionPercent,
  ]);

  const effectiveQuote = useMemo(
    () => ({ ...quote, calculation: effectiveCalculation }),
    [quote, effectiveCalculation]
  );

  const mainAgent = proposal.agents?.[0];
  const prefillBrokerName = mainAgent?.name ?? '';
  const prefillCommissionPercent = deriveCommissionPercent(mainAgent);
  const prefillParticipationPercent = deriveParticipationPercent(mainAgent);
  const prefillCoBrokers = useMemo(() => deriveCoBrokers(proposal), [proposal]);
  const prefillBrokerAgentCode = mainAgent?.agentCode ?? '';

  const goToTab = (tab: AnalysisTab) => {
    setActiveTab(tab);
    document.querySelector('main')?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goToPrevTab = () => {
    const idx = TAB_ORDER.indexOf(activeTab);
    if (idx > 0) {
      goToTab(TAB_ORDER[idx - 1]);
      return;
    }
    void navigate({ to: '/analyses' });
  };

  const goToNextTab = () => {
    const idx = TAB_ORDER.indexOf(activeTab);
    if (idx < TAB_ORDER.length - 1) {
      goToTab(TAB_ORDER[idx + 1]);
    }
  };

  const handleCalculate = useCallback(
    (discountLoading?: DiscountLoading, onSuccess?: () => void) => {
      if (calculateAnalysis.isPending) return;

      const formValues = mainForm.state.values as Record<string, unknown>;

      // O StepCommercialization chama `updateCommercialization` e o `onNext`
      // na mesma passada — o contexto ainda não propagou aqui no primeiro
      // clique. Lê os campos direto do form (sempre atuais) e usa o contexto
      // apenas como fallback.
      const asFilledString = (value: unknown) =>
        typeof value === 'string' && value.trim() !== '' ? value : undefined;
      const commercialization = {
        ...summary.commercialization,
        brokerName:
          asFilledString(formValues.brokerName) ??
          summary.commercialization.brokerName,
        commissionPercent:
          asFilledString(formValues.commissionPercent) ??
          summary.commercialization.commissionPercent,
        participationPercent:
          asFilledString(formValues.participationPercent) ??
          summary.commercialization.participationPercent,
      };

      const payload = buildAnalysisPayload(
        proposal,
        formValues,
        commercialization,
        analysisLocations
      );

      // Flexibilização comercial (agravo/desconto) é aplicada sobre o prêmio
      // líquido no recálculo — força o step CALCULATION para que o backend
      // recalcule IOF, prêmio total e parcelamento.
      if (discountLoading) {
        payload.step = 'CALCULATION';
        payload.discountLoadingType = discountLoading.discountLoadingType;
        payload.discountLoadingRate = discountLoading.discountLoadingRate;
      }

      calculateAnalysis.mutate(
        { policyId, payload },
        {
          onSuccess: () => onSuccess?.(),
          onError: error => {
            toaster.create({
              type: 'error',
              title: 'Erro ao calcular',
              description:
                error instanceof Error
                  ? error.message
                  : 'Não foi possível recalcular a cotação. Tente novamente.',
            });
          },
        }
      );
    },
    [
      analysisLocations,
      calculateAnalysis,
      mainForm,
      policyId,
      proposal,
      summary.commercialization,
    ]
  );

  const handleConfirm = useCallback(() => {
    if (submitAnalysisCalculation.isPending) return;

    submitAnalysisCalculation.mutate(
      { policyId },
      {
        onSuccess: () => {
          toaster.create({
            type: 'success',
            title: 'Cálculo enviado com sucesso',
          });
          void navigate({ to: '/quotes' });
        },
        onError: error => {
          toaster.create({
            type: 'error',
            title: 'Erro ao enviar cálculo',
            description:
              error instanceof Error
                ? error.message
                : 'Não foi possível enviar o cálculo da análise. Tente novamente.',
          });
        },
      }
    );
  }, [submitAnalysisCalculation, policyId, navigate]);

  const handleReject = useCallback(
    (description: string) => {
      if (rejectAnalysis.isPending) return;

      const trimmed = description.trim();
      if (!trimmed) {
        toaster.create({
          type: 'error',
          title: 'Informe a descrição',
          description:
            'Preencha o campo "Observações da Análise" antes de recusar.',
        });
        return;
      }

      rejectAnalysis.mutate(
        { policyId, description: trimmed },
        {
          onSuccess: async result => {
            if (result.refusalLetterGenerated) {
              // Reaproveita o key/url do reject-analysis quando o backend já
              // expôs a chave; caso contrário consulta o GET /documents para
              // obter uma URL pré-assinada fresca.
              try {
                const docResponse = await businessQuotesService.getDocument(
                  policyId,
                  'refusal_letter'
                );
                if (docResponse.success && docResponse.data) {
                  const filename =
                    docResponse.data.key.split('/').pop() ||
                    `carta-recusa-${policyId}.pdf`;
                  await downloadFromSignedUrl(docResponse.data.url, filename);
                  toaster.create({
                    type: 'success',
                    title: 'Cotação recusada',
                    description:
                      'A carta de recusa foi gerada e o download começou.',
                  });
                } else {
                  toaster.create({
                    type: 'success',
                    title: 'Cotação recusada',
                    description:
                      'A carta de recusa foi gerada, mas não foi possível baixar automaticamente. Acesse a tela de detalhes da cotação para baixar.',
                  });
                }
              } catch {
                toaster.create({
                  type: 'success',
                  title: 'Cotação recusada',
                  description:
                    'A carta de recusa foi gerada, mas não foi possível baixar automaticamente. Acesse a tela de detalhes da cotação para baixar.',
                });
              }
            } else {
              toaster.create({
                type: 'success',
                title: 'Cotação recusada',
                description:
                  'A carta de recusa não foi gerada (template indisponível).',
              });
            }
            void navigate({ to: '/analyses' });
          },
          onError: error => {
            toaster.create({
              type: 'error',
              title: 'Erro ao recusar',
              description:
                error instanceof Error
                  ? error.message
                  : 'Não foi possível recusar a cotação. Tente novamente.',
            });
          },
        }
      );
    },
    [rejectAnalysis, policyId, navigate]
  );

  return (
    <TabsComponent.Root
      value={activeTab}
      onValueChange={details => setActiveTab(details.value as AnalysisTab)}
    >
      <TabsComponent.List className='grid grid-cols-2 overflow-hidden rounded-2xl bg-alba-gray-100 sm:grid-cols-4'>
        <TabsComponent.Trigger
          value='insurance'
          className='min-h-[60px] rounded-bl-none! px-2 py-3 text-center text-sm leading-tight sm:min-h-[72px] sm:rounded-bl-2xl!'
        >
          Dados do Seguro
        </TabsComponent.Trigger>
        <TabsComponent.Trigger
          value='risk'
          className='min-h-[60px] px-2 py-3 text-center text-sm leading-tight sm:min-h-[72px]'
        >
          Dados do Risco
        </TabsComponent.Trigger>
        <TabsComponent.Trigger
          value='commercialization'
          className='min-h-[60px] px-2 py-3 text-center text-sm leading-tight sm:min-h-[72px]'
        >
          Comercialização
        </TabsComponent.Trigger>
        <TabsComponent.Trigger
          value='calculation'
          className='min-h-[60px] rounded-tr-none! px-2 py-3 text-center text-sm leading-tight sm:min-h-[72px] sm:rounded-tr-2xl!'
        >
          Cálculo
        </TabsComponent.Trigger>
      </TabsComponent.List>

      <TabsComponent.Content value='insurance'>
        <div className='flex flex-col gap-6'>
          <AnalysisCriteriaAlert messages={underwritingMessages} />
          <div className='w-full rounded-2xl bg-white p-4 shadow-md sm:p-8'>
            <RequiredTooltipProvider restartKey='insurance'>
              <StepInsuredData
                // @ts-expect-error - incompatibilidade conhecida com formulários compostos
                form={mainForm}
                onNext={goToNextTab}
                onBack={goToPrevTab}
                productId={quote.productId}
                productCode={quote.productCode}
                category='BUSINESS_INSURANCE'
                stepCode='CLIENT_INFO'
                skipBackendSubmit
              />
            </RequiredTooltipProvider>
          </div>
        </div>
      </TabsComponent.Content>

      <TabsComponent.Content value='risk'>
        <div className='flex flex-col gap-6'>
          <AnalysisCriteriaAlert messages={underwritingMessages} />
          <div className='w-full rounded-2xl bg-white p-4 shadow-md sm:p-8'>
            <StepRiskBusinessData
              // @ts-expect-error - incompatibilidade conhecida com formulários compostos
              form={mainForm}
              onNext={goToNextTab}
              onBack={goToPrevTab}
              stepCode='RISK_BUSINESS'
              compactButtons
              initialRisks={initialRisks}
              skipBackendSubmit
              onLocationsChange={setAnalysisLocations}
            />
          </div>
        </div>
      </TabsComponent.Content>

      <TabsComponent.Content value='commercialization'>
        <div className='flex flex-col gap-6'>
          <AnalysisCriteriaAlert messages={underwritingMessages} />
          <div className='w-full rounded-2xl bg-white p-4 shadow-md sm:p-8'>
            <StepCommercialization
              // @ts-expect-error - incompatibilidade conhecida com formulários compostos
              form={mainForm}
              // Calcula na subscrição e só então avança para a aba de cálculo.
              onNext={() => handleCalculate(undefined, () => goToTab('calculation'))}
              onBack={goToPrevTab}
              productId={quote.productId}
              productCode={quote.productCode}
              prefillBrokerName={prefillBrokerName}
              prefillCommissionPercent={prefillCommissionPercent}
              prefillParticipationPercent={prefillParticipationPercent}
              prefillCoBrokers={prefillCoBrokers}
              prefillBrokerAgentCode={prefillBrokerAgentCode}
              stepCode='COMMERCIALIZATION'
              skipBackendSubmit
              isSubmitting={calculateAnalysis.isPending}
            />
          </div>
        </div>
      </TabsComponent.Content>

      <TabsComponent.Content value='calculation'>
        <div className='flex flex-col gap-6'>
          <AnalysisCriteriaAlert messages={underwritingMessages} />
          <Step4AnalysisCalculation
            quote={effectiveQuote}
            locations={calculationLocations}
            productId={quote.productId}
            paymentInfo={proposal.paymentInfo}
            onBack={goToPrevTab}
            onReject={handleReject}
            onSubmit={handleConfirm}
            onExit={() => void navigate({ to: '/analyses' })}
            isSubmitting={
              calculateAnalysis.isPending ||
              submitAnalysisCalculation.isPending ||
              rejectAnalysis.isPending
            }
            onRecalculate={handleCalculate}
          />
        </div>
      </TabsComponent.Content>
    </TabsComponent.Root>
  );
}
